<script lang="ts">
	import type { Coordinates } from '$lib/components/map/types';
	import type { HRTPoiData } from '$lib/components/pois/card/factories-utils';
	import type {
		PoiData,
		SplittedAddress,
		SummaryPoiData,
	} from '$lib/components/pois/types';
	import type { TrafficInformationSummary } from '../traffic/types';
	import { page } from '$app/state';
	import { type CoordinatesInput, ReverseGeocodeStore } from '$houdini';
	import AddressSummary from '$lib/components/address/summary/AddressSummary.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import PoiSummary from '$lib/components/pois/summary/PoiSummary.svelte';
	import { ROUTE_ROUTES } from '$lib/constants/route';
	import { isMobile } from '$lib/services/mediaQuery/screenSize';
	import { searchExtensionTouchpoint } from '$lib/services/searchExtension';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { trackAddItineraryEvent } from '$lib/services/tracking/googleAnalytics/pois';
	import { getSearchParamsStore, gotoWithParams } from '$lib/services/urls/searchParams';
	import { watch } from 'runed';
	import { get } from 'svelte/store';
	import { computeSearchParamsItinerary } from '../itinerary/util';
	import { isHRTPoi, isSummaryPoiData } from '../pois/utils';
	import TrafficSummary from '../traffic/TrafficSummary.svelte';

	interface Props {
		poiData?: PoiData | undefined;
		trafficEventData?: TrafficInformationSummary | undefined;
		onCancelPosition: (cancelAction: boolean) => void;
		onCloseTrafficSummary: () => void;
		onSummaryVisible: (visible: boolean) => void;
	}

	const {
		poiData,
		trafficEventData,
		onCancelPosition,
		onCloseTrafficSummary,
		onSummaryVisible,
	}: Props = $props();

	const { itineraryStore, isMapFullscreen, clickedPosition, hoveredPoiId } = getMapLayoutContext();
	const searchParams = getSearchParamsStore();

	interface AddressResult {
		splittedAddress: SplittedAddress | null;
		formattedAddress?: string;
		countryCode?: string;
		city?: string;
		zipCode?: string;
		loading: boolean;
		error: boolean;
		notFound: boolean;
	};

	let reverseGeocodeStore: ReverseGeocodeStore | null = null;
	let addressResult: AddressResult | undefined = $state(undefined);

	const clickedPositionDerived = $derived($clickedPosition);
	const coordinates = $derived.by(() => {
		if (poiData) {
			return poiData.coordinates;
		}
		return clickedPositionDerived;
	});
	const { itineraryResultsStore } = $derived(page.data);

	function lookupAddress(lookupPos: Coordinates | undefined, retry?: boolean) {
		if (poiData && lookupPos) {
			return;
		}

		if (!lookupPos) {
			addressResult = undefined;
			$clickedPosition = undefined;
			return;
		}

		const position: CoordinatesInput = lookupPos;
		if (!reverseGeocodeStore) {
			reverseGeocodeStore = new ReverseGeocodeStore();
		}
		addressResult = {
			loading: true,
			error: !!retry,
			notFound: false,
			splittedAddress: null,
		};
		reverseGeocodeStore
			.fetch({ variables: { position }, blocking: true })
			.then((result) => {
				if (result.data?.reverseGeocode.__typename === 'ReverseGeocodeSuccessResult') {
					const splittedAddress: SplittedAddress = {
						...result.data.reverseGeocode.splittedAddress,
					};
					if (splittedAddress && !splittedAddress.firstLine?.trim()) {
						splittedAddress.firstLine = splittedAddress.secondLine;
						splittedAddress.secondLine = ' ';
					}
					addressResult = {
						splittedAddress,
						formattedAddress: result.data.reverseGeocode.address?.formattedAddress || undefined,
						countryCode: result.data.reverseGeocode.address.country?.code,
						city: result.data.reverseGeocode.address.city || undefined,
						zipCode: result.data.reverseGeocode.address.zipCode || undefined,
						loading: false,
						error: false,
						notFound: false,
					};
				} else {
					addressResult = {
						notFound: true,
						loading: false,
						error: false,
						splittedAddress: null,
					};
				}
			})
			.catch(() => {
				addressResult = {
					error: true,
					loading: false,
					notFound: false,
					splittedAddress: null,
				};
			})
			.finally(() => {
				track({
					action: 'map_marker_placed',
					payload: {
						action: 'click',
						result: addressResult?.formattedAddress,
						country: addressResult?.countryCode,
						city: addressResult?.city,
						zipcode: addressResult?.zipCode,
					},
				});
			});
	}

	async function addAddressWaypoint() {
		if ((!addressResult && !poiData) || !coordinates) {
			return;
		}

		itineraryResultsStore?.reset();

		if ($isMapFullscreen) {
			$isMapFullscreen = false;
		}

		itineraryStore.addToItinerary({
			coordinates,
			label: addressResult?.formattedAddress || poiData?.name,
		});
		const itinerary = await computeSearchParamsItinerary(get(itineraryStore.itinerary));

		track({
			action: 'map_add_step',
			payload: {
				action: 'click',
				result: addressResult?.formattedAddress || poiData?.name,
				country: addressResult?.countryCode || (poiData?.address.country?.code ?? undefined),
				city: addressResult?.city || (poiData?.address.city ?? undefined),
				zipcode: addressResult?.zipCode || (poiData?.address.zipCode ?? undefined),
			},
		});

		searchExtensionTouchpoint.activate('map_add_step_compute');

		if (itineraryStore.isCandidateForComputeItinerary()) {
			$searchParams.itinerary = itinerary;
		} else {
			gotoWithParams('/routes', { ...$searchParams, itinerary });
		}

		onCancelPosition(false);
	}

	async function handleAddItineraryClick(poi: SummaryPoiData | HRTPoiData) {
		if (!poi || !itineraryStore) return;

		trackAddItineraryEvent(poi, 'poimap_itinerary_button');

		itineraryStore.addToItinerary({ coordinates: poi.coordinates, label: poi.name });
		const itinerary = await computeSearchParamsItinerary(get(itineraryStore.itinerary));

		$hoveredPoiId = null;
		gotoWithParams(ROUTE_ROUTES, {
			...$searchParams,
			itinerary,
		});
	}

	function handleCardClick(e: MouseEvent) {
		e.stopPropagation();
		$hoveredPoiId = null;
	}

	const canShowSummaryCard = $derived.by(() => {
		return (poiData && (isSummaryPoiData(poiData) || (isHRTPoi(poiData) && $isMobile))) || addressResult || trafficEventData;
	});

	watch(() => canShowSummaryCard, (canShowSummaryCard) => {
		onSummaryVisible(!!canShowSummaryCard);
	});

	watch(() => coordinates, () => {
		lookupAddress(coordinates);
	});
</script>

{#if canShowSummaryCard}
	<div
		data-testid="floating-summary"
	>
		{#if poiData && (isSummaryPoiData(poiData) || (isHRTPoi(poiData) && $isMobile))}
			<PoiSummary
				poiData={poiData as SummaryPoiData | HRTPoiData}
				onAddClick={addAddressWaypoint}
				onAddItineraryClick={() => handleAddItineraryClick(poiData as SummaryPoiData | HRTPoiData)}
				onCardClick={handleCardClick}
			/>
		{:else if addressResult}
			<AddressSummary
				address={addressResult.splittedAddress}
				isLoading={addressResult.loading}
				hasError={addressResult.error}
				notFound={addressResult.notFound}
				onRetryClick={() => lookupAddress($clickedPosition)}
				onAddClick={addAddressWaypoint}
				{onCancelPosition}
			/>
		{:else if trafficEventData}
			<TrafficSummary trafficEvent={trafficEventData} {onCloseTrafficSummary} />
		{/if}
	</div>
{/if}
